import _ from 'underscore';
import countlyVue from '../../compat/vue-core';
import i18n from '../../../../i18n';

const AbstractListBox = countlyVue.components.BaseComponent.extend({
  props: {
    options: { type: Array },
    bordered: { type: Boolean, default: true },
    margin: { type: Boolean, default: true },
    skin: {
      type: String,
      default: 'default',
      required: false,
      validator(val) {
        return val === 'default' || val === 'jumbo';
      },
    },
    disabled: { type: Boolean, default: false, required: false },
    height: { type: [Number, String], default: 300, required: false },
    expandOnHover: { type: Boolean, default: false, required: false },
    hasRemovableOptions: { type: Boolean, default: false, required: false },
    noMatchFoundPlaceholder: { type: String, default: i18n.tc('common.search.no-match-found'), required: false },
  },
  methods: {
    onRemoveOption(option) {
      this.$emit('remove-option', option);
    },
    navigateOptions() {
      if (!this.visible) {
        this.visible = true;
      }
    },
    handleItemClick(option) {
      if (!this.disabled) {
        this.$emit('input', option.value);
        this.$emit('change', option.value);
      }
    },
    handleItemHover(option) {
      this.hovered = option.value;
    },
    handleBlur() {
      this.hovered = this.value;
      this.focused = false;
    },
    handleHover() {
      this.focused = true;
    },
  },
  data() {
    return {
      hovered: null,
      focused: false,
      scrollCfg: {
        scrollPanel: {
          initialScrollX: false,
          scrollingX: false,
        },
        rail: {
          gutterOfSide: '0px',
        },
        bar: {
          background: '#A7AEB8',
          size: '6px',
          specifyBorderRadius: '3px',
          keepShow: false,
        },
      },
    };
  },
  computed: {
    topClasses() {
      const classes = {
        'is-focus': this.focused,
        'cly-vue-listbox--bordered': this.bordered,
        'cly-vue-listbox--disabled': this.disabled,
        'cly-vue-listbox--has-margin': this.margin && !(this.skin === 'jumbo'),
        'is-expanded': this.expandOnHover && this.focused,
        'is-expandable': this.expandOnHover,
      };
      classes[`cly-vue-listbox--has-${this.skin}-skin`] = true;
      return classes;
    },
    wrapperStyle() {
      if (this.height !== 'auto') {
        return {
          'max-height': `${this.height}px`,
        };
      }
      return false;
    },
    vueScrollStyle() {
      if (this.searchable) {
        return {
          height: 'calc(100% - 58px)',
        };
      }

      return false;
    },
  },
});

const TabbedOptionsMixin = {
  props: {
    options: {
      type: Array,
      default() {
        return [];
      },
    },
    hideDefaultTabs: { type: Boolean, default: false },
    allPlaceholder: { type: String, default: 'All' },
    hideAllOptionsTab: { type: Boolean, default: false },
    onlySelectedOptionsTab: { type: Boolean, default: false },
    prefixLabelWithTabId: { type: Boolean, default: false },
  },
  data() {
    return {
      activeTabId: null,
    };
  },
  computed: {
    missingOptions() {
      let query = this.value;

      if (!query) {
        return [];
      }

      if (!Array.isArray(query)) {
        query = [query];
      }
      return query.filter((key) => !this.flatOptions.some((option) => key === option.value)).map((missingKey) => ({
        label: missingKey,
        value: missingKey,
      }));
    },
    missingOptionsTab() {
      if (this.missingOptions.length) {
        return {
          name: '__missing',
          label: '?',
          options: this.missingOptions,
        };
      }
      return null;
    },
    selectedOptions() {
      if (!this.onlySelectedOptionsTab && !this.flatOptions.length && !this.missingOptions.length) {
        return [];
      }
      const missingOptions = this.missingOptions || [];

      if (this.onlySelectedOptionsTab) {
        let selected = null;
        if (Array.isArray(this.value)) {
          selected = this.value.slice();
        } else {
          selected = [this.value];
        }
        return this.flatOptions.reduce((acc, item) => {
          const idx = acc.indexOf(item.value);
          if (idx > -1) {
            acc.splice(idx, 1);
          }
          return acc;
        }, selected).map((missingKey) => ({
          label: missingKey,
          value: missingKey,
        })).concat(this.flatOptions);
      }

      if (Array.isArray(this.value)) {
        return missingOptions.concat(this.flatOptions.filter((item) => this.value.indexOf(item.value) > -1));
      }

      let matching = this.flatOptions.filter((item) => item.value === this.value);
      if (this.prefixLabelWithTabId && matching.length) {
        const selectedTab = this.publicTabs.filter((tab) => this.val2tab[this.value] === tab.name);
        if (selectedTab.length) {
          const valueTab = selectedTab[0];
          const singleOption = valueTab.options && valueTab.options.length === 1 && this.singleOptionSettings.hideList;
          if (!singleOption) {
            matching = [{
              label: `${selectedTab[0].label}, ${matching[0].label}`,
              value: matching[0].value,
            }];
          }
        }
      }
      return missingOptions.concat(matching);
    },
    selectedOptionsTab() {
      if (this.hasSelectedOptionsTab && this.selectedOptions && this.selectedOptions.length) {
        return {
          name: '__selected',
          label: 'Selected',
          options: this.selectedOptions,
        };
      }
      return null;
    },
    hasAllOptionsTab() {
      if (this.hideAllOptionsTab || this.mode === 'multi-check-sortable') {
        return false;
      }
      return true;
    },
    hasSelectedOptionsTab() {
      return this.onlySelectedOptionsTab || (this.isMultiple && this.remote && this.value && this.value.length > 0);
    },
    showTabs() {
      if (this.onlySelectedOptionsTab) {
        return false;
      }
      if (this.hasSelectedOptionsTab) {
        return true;
      }
      if (!this.options || !this.options.length) {
        return false;
      }
      return !!this.options[0].options;
    },
    publicTabs() {
      const { missingOptionsTab } = this;
      const { selectedOptionsTab } = this;
      const prefixTabs = [];
      const postfixTabs = [];

      if (selectedOptionsTab) {
        postfixTabs.push(selectedOptionsTab);
      } else if (missingOptionsTab) {
        prefixTabs.push(missingOptionsTab);
      }

      if (this.showTabs && this.hasAllOptionsTab) {
        prefixTabs.unshift({
          name: '__all',
          label: this.allPlaceholder,
          options: this.flatOptions,
        });
      }

      if (!this.showTabs && !this.onlySelectedOptionsTab) {
        prefixTabs.unshift({
          name: '__root',
          label: '__root',
          options: this.options,
        });
      }

      if (!this.showTabs) {
        return prefixTabs.concat(postfixTabs);
      }
      return prefixTabs.concat(this.options).concat(postfixTabs);
    },
    flatOptions() {
      if ((!this.showTabs && !this.onlySelectedOptionsTab) || !this.options.length) {
        return this.options;
      }
      return this.options.reduce((items, tab) => items.concat(tab.options), []);
    },
    val2tab() {
      if (!this.publicTabs.length) {
        return {};
      }
      return this.publicTabs.reduce((items, tab) => {
        tab.options.forEach((opt) => {
          /* eslint-disable no-param-reassign */
          items[opt.value] = tab.name;
          /* eslint-enable no-param-reassign */
        });
        return items;
      }, {});
    },
  },
  methods: {
    updateTabFn(tabId) {
      this.activeTabId = tabId;
    },
    determineActiveTabId() {
      this.$nextTick(() => {
        if (this.onlySelectedOptionsTab) {
          this.activeTabId = '__selected';
        } else if (!this.showTabs) {
          this.activeTabId = '__root';
        } else if (this.value && this.val2tab[this.value]) {
          this.activeTabId = this.val2tab[this.value];
        } else if (this.hasAllOptionsTab) {
          this.activeTabId = '__all';
        } else if (!this.activeTabId || this.activeTabId === '__all' || this.activeTabId === '__root') {
          this.activeTabId = this.publicTabs[0].name;
        }
      });
    },
    navigateToFirstRegularTab() {
      if (this.options && this.options[0]) {
        this.activeTabId = this.options[0].name;
      }
    },
  },
  watch: {
    value() {
      this.determineActiveTabId();
    },
    hasAllOptionsTab() {
      this.determineActiveTabId();
    },
    showTabs() {
      this.determineActiveTabId();
    },
    'flatOptions.length': function (newVal) {
      if (!newVal && this.hasSelectedOptionsTab) {
        this.activeTabId = '__selected';
      }
    },
  },
};

const SearchableOptionsMixin = {
  props: {
    searchable: { type: Boolean, default: true },
    searchPlaceholder: { type: String, default: 'Search' },
  },
  data() {
    return {
      searchQuery: '',
      isQueryPending: false,
    };
  },
  mounted() {
    this.callRemote();
  },
  watch: {
    searchQuery: _.debounce(function (newVal) {
      this.callRemote(newVal);
    }, 500),
  },
  methods: {
    callRemote(query) {
      if (this.remote && this.remoteMethod) {
        this.isQueryPending = true;
        Promise.resolve(this.remoteMethod(query || '')).finally(() => {
          this.isQueryPending = false;
          if (this.updateDropdown) {
            this.updateDropdown();
          }
          if (!this.onlySelectedOptionsTab) {
            this.navigateToFirstRegularTab();
          }
        });
      }
    },
    getMatching(options) {
      if (!this.searchQuery || !this.searchable || this.remote) {
        return options;
      }
      const query = (`${this.searchQuery}`).toLowerCase();
      return options.filter((option) => {
        if (!option) {
          return false;
        }
        const compareTo = option.label || option.value || '';
        return compareTo.toLowerCase().indexOf(query) > -1;
      });
    },
  },
};

export {
  AbstractListBox,
  SearchableOptionsMixin,
  TabbedOptionsMixin,
};
